import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fmt, fmtDate } from "./ui";
import "./quote-document.scss";

export type QuoteSnapshot = {
  quote_no: string;
  issued_at: string;
  valid_until: string;
  price_list: string;
  order: { id: number; order_no: string; name: string | null };
  supplier: { name: string };
  customer: { name: string | null; site: string | null; address: string | null; payment_terms: string | null; discount_percent: string };
  items: Array<{ id: number; name: string; diameter: string | null; quantity: number; weight_ton: string; material_per_ton: string | null; charge_type: string; total: string }>;
  totals: Record<string, string | number>;
};

export function QuoteTotals({ snapshot }: { snapshot: QuoteSnapshot }) {
  const { t } = useTranslation("supplierPricing");
  const value = (key: string) => Number(snapshot.totals[key] ?? 0);
  const rows: [string, number][] = [
    [t("order.material", { tons: value("weight_ton").toFixed(3) }), value("material")],
    [t("analysis.processing"), value("bend") + value("cut") + value("units")],
    [t("order.transport"), value("transport")],
    [t("order.discount", { pct: snapshot.customer.discount_percent }), value("discount")],
    [t("order.subtotal"), value("subtotal")],
    [t("order.vat", { pct: value("vat_percent") }), value("vat")],
    [t("order.total"), value("total")],
  ];
  return <div className="eb-quote-totals">{rows.map(([label, amount]) =>
    <div key={label}><span>{label}</span><b dir="ltr">{fmt(amount)} ₪</b></div>
  )}</div>;
}

export default function QuoteDocument({ snapshot, onReady }: { snapshot: QuoteSnapshot; onReady?: () => void }) {
  const { t, i18n } = useTranslation("supplierPricing");
  const initialPages = () => {
    const rows: typeof snapshot.items[] = [];
    for (let index = 0; index < snapshot.items.length; index += 12) rows.push(snapshot.items.slice(index, index + 12));
    return rows;
  };
  const [pagination, setPagination] = useState({ snapshot, rows: initialPages() });
  const documentRef = useRef<HTMLDivElement>(null);
  const rowsByPage = pagination.snapshot === snapshot ? pagination.rows : initialPages();
  if (pagination.snapshot !== snapshot) setPagination({ snapshot, rows: rowsByPage });
  // Split overflowing tables before paint. Long descriptions must not be cut off by A4 capture.
  useLayoutEffect(() => {
    let active = true;
    let frame: number;
    const paginate = () => {
      if (!active) return;
      const sections = documentRef.current?.querySelectorAll<HTMLElement>(".eb-quote-page");
      if (!sections) return;
      // Bootstrap briefly hides a modal while mounting. Wait for its actual layout.
      if (Array.from(sections).some(section => !section.getBoundingClientRect().height)) {
        frame = requestAnimationFrame(paginate);
        return;
      }
      let changed = false;
      const rows = rowsByPage.flatMap((items, index) => {
        const section = sections[index];
        const table = section.querySelector("table");
        const bottom = section.getBoundingClientRect().bottom - 96;
        if (items.length > 1 && table && table.getBoundingClientRect().bottom > bottom) {
          changed = true;
          const middle = Math.ceil(items.length / 2);
          return [items.slice(0, middle), items.slice(middle)];
        }
        return [items];
      });
      if (changed) setPagination({ snapshot, rows });
      else if (document.fonts?.status !== "loading") onReady?.();
      else frame = requestAnimationFrame(paginate);
    };
    paginate();
    document.fonts?.ready.then(paginate);
    return () => { active = false; cancelAnimationFrame(frame); };
  }, [snapshot, rowsByPage, onReady]);
  // Totals have their own page, preserving the full summary in every export.
  const pages = [...rowsByPage, []];
  return <div className="eb-quote-document" ref={documentRef} dir={i18n.dir()} lang={i18n.language}>
    {pages.map((rows, page) => <section className="pdf-document-page eb-quote-page" key={page}>
      <header>
        <h2>{snapshot.supplier.name}</h2>
        <h3>{t("quote.title", { no: snapshot.quote_no })}</h3>
        <p>{t("quote.date")}: {fmtDate(snapshot.issued_at)} · {t("quote.validUntil")}: {fmtDate(snapshot.valid_until)}</p>
        <p>{t("quote.order")}: <b dir="ltr">{snapshot.order.order_no}</b> {snapshot.order.name}</p>
        <p>{t("order.priceList")}: {t(snapshot.price_list === "default" ? "tabs.default" : "order.customerList")}</p>
        <p>{t("quote.to")}: {snapshot.customer.name || "—"}</p>
        {snapshot.customer.site && <p>{t("quote.site")}: {snapshot.customer.site} {snapshot.customer.address}</p>}
        {snapshot.customer.payment_terms && <p>{t("quote.terms")}: {t(`customerTab.terms.${snapshot.customer.payment_terms}`)}</p>}
      </header>
      {rows.length ? <table>
        <thead><tr><th>#</th><th>{t("quote.description")}</th><th>{t("quote.qty")}</th><th>{t("quote.weight")}</th><th>{t("quote.perTon")}</th><th>{t("quote.sum")}</th></tr></thead>
        <tbody>{rows.map((row, index) => <tr key={row.id} data-quote-item-id={row.id}>
          <td>{rowsByPage.slice(0, page).reduce((count, items) => count + items.length, 0) + index + 1}</td>
          <td>{row.name} · Φ{row.diameter || "—"} · {t(`charge.${row.charge_type}`)}</td>
          <td dir="ltr">{row.quantity}</td><td dir="ltr">{fmt(Number(row.weight_ton) * 1000, 1)}</td>
          <td dir="ltr">{row.material_per_ton === null ? "—" : fmt(Number(row.material_per_ton))}</td>
          <td dir="ltr">{fmt(Number(row.total))}</td>
        </tr>)}</tbody>
      </table> : <><QuoteTotals snapshot={snapshot} /><p className="eb-quote-signature">{t("quote.customerSign")}</p></>}
      <footer dir="ltr" data-pdf-page-counter>{page + 1} / {pages.length}</footer>
    </section>)}
  </div>;
}
